import { ROUTES } from 'constants/routes';
import {
  DEFAULT_PRIMARY_SCHOOL_SLUG,
  learnHrefForPrimarySchool,
  type PrimarySchoolSlug,
} from 'utils/primarySchool';

export type LearnerNavLinkKey = 'schools' | 'default';

export type LearnerNavLocation = {
  pathname: string;
  search: string;
  state: unknown;
};

/** Canonical school learn bases - keep in sync with AppLayout full-bleed + bottom nav. */
export const SCHOOL_LEARN_ROUTES = [
  ROUTES.PARASHARI_LEARN,
  ROUTES.JAIMINI_LEARN,
  ROUTES.BNN_LEARN,
  ROUTES.KP_LEARN,
  ROUTES.LAL_KITAB_LEARN,
  ROUTES.NUMEROLOGY_LEARN,
  ROUTES.SURYA_SIDDHANTA_LEARN,
  ROUTES.PANCHANG_LEARN,
  ROUTES.VASTU_LEARN,
  ROUTES.SWARA_VIGYAN_LEARN,
  ROUTES.YANTRA_VIDYA_LEARN,
  ROUTES.TANTRA_LEARN,
] as const;

export function isSchoolLearnPath(pathname: string): boolean {
  return SCHOOL_LEARN_ROUTES.some((route) => pathname.startsWith(route));
}

/**
 * Routes whose body is rendered by the hand-maintained school modules
 * (`src/schools/*`). Those pages were authored against the larger reading scale
 * that `parashari-styles.css` layers on top of Tailwind, so AppLayout tags their
 * subtree with `school-type-scale` and everything else keeps the platform fluid
 * ramp. See takshify-shared/docs/platform/13-technical-debt.md § P9.
 */
export const SCHOOL_TYPE_SCALE_ROUTES = [
  ...SCHOOL_LEARN_ROUTES,
  ROUTES.PROFILE,
  ROUTES.SADHANA,
  ROUTES.SETTINGS,
] as const;

export function isSchoolTypeScalePath(pathname: string): boolean {
  return SCHOOL_TYPE_SCALE_ROUTES.some((route) => pathname.startsWith(route));
}

export function isSchoolsAreaPath(pathname: string): boolean {
  if (pathname === ROUTES.SCHOOLS) return true;
  if (!pathname.startsWith('/schools/')) return false;
  if (pathname.startsWith('/admin')) return false;
  return true;
}

/**
 * Learn tab destination: stay on current school when already learning;
 * otherwise open the learner's primary school (default Parashari).
 */
export function resolveLearnHref(
  pathname: string,
  primarySchoolSlug: PrimarySchoolSlug | string | null | undefined = DEFAULT_PRIMARY_SCHOOL_SLUG,
): string {
  const match = SCHOOL_LEARN_ROUTES.find((route) => pathname.startsWith(route));
  if (match) return match;
  return learnHrefForPrimarySchool(primarySchoolSlug ?? DEFAULT_PRIMARY_SCHOOL_SLUG);
}

export function isLearnerNavLinkActive(
  link: { to: string; navKey?: LearnerNavLinkKey },
  location: LearnerNavLocation,
): boolean {
  const { pathname } = location;

  if (link.navKey === 'schools') {
    return isSchoolsAreaPath(pathname);
  }

  return pathname === link.to || (link.to !== '/' && pathname.startsWith(link.to));
}
